import React, { useState, useMemo } from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import CircularProgress from "@mui/material/CircularProgress";
import { ConventionLogoSquare } from "../events/ConventionLogo";
import { formatConventionDate, formatLocation } from "@seabrookstudios/pitch2table-core";
import { useAllConventions, useMyProfile, useUserHistoricalConventions } from "@seabrookstudios/pitch2table";
import { FullScreenDialog } from "./FullScreenDialog";
import { CircularProgressSize } from "../theme";
import ListItem from "@mui/material/ListItem";
import LinkOff from "@mui/icons-material/LinkOff";
import { DateTime } from "luxon";
import { SelectableListEntriesDialog } from "./SelectableListEntriesDialog";
import { FilterableConventionsList } from "../events/FilterableConventionsList";
import { ArrowListItem } from "./ArrowListItem";
import { Path } from "../Path";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").ConventionId|undefined} props.value - Selected convention ID
 * @param {function(import("@seabrookstudios/pitch2table-core").ConventionId|null): void} props.onChange - Callback when selection changes
 */
export const SelectConventionDialog = ({ value, onChange }) => {
  const { data: allConventions = [], isLoading } = useAllConventions();
  const [forceClose, setForceClose] = useState(false);

  /**
   * @param {import("@seabrookstudios/pitch2table-core").ConventionId} conventionId
   */
  const handleSelectConvention = (conventionId) => {
    onChange(conventionId);
    setForceClose(true);
  };

  if (isLoading) {
    return (
      <FullScreenDialog title="Select Convention" canSave={true}>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <CircularProgress size={CircularProgressSize} />
        </div>
      </FullScreenDialog>
    );
  }

  const LinkedEvent = () => {
    if (!value) {
      return (
        <ListItem>
          <ListItemText primary="Linked Event" secondary="Tap to select" />
        </ListItem>
      );
    }

    const convention = allConventions.find((c) => c.id === value);
    if (!convention) {
      return (
        <ListItem>
          <ListItemText primary="Linked Event" secondary="Tap to select" />
        </ListItem>
      );
    }

    return (
      <ListItem>
        <ListItemAvatar>
          <ConventionLogoSquare convention={convention} />
        </ListItemAvatar>
        <ListItemText
          primary={convention.name}
          secondary={`${formatConventionDate(convention)} • ${formatLocation(convention)}`}
        />
      </ListItem>
    );
  };

  return (
    <FullScreenDialog
      id="select-convention"
      title="Select Convention"
      canSave={true}
      forceClose={forceClose}
      onOpen={() => setForceClose(false)}
      onDeleteHandler={() => onChange(null)}
      DeleteIcon={<LinkOff />}
      CustomControl={<LinkedEvent />}
    >
      <List>
        {allConventions.map((convention) => (
          <ListItemButton key={convention.id} onClick={() => handleSelectConvention(convention.id)}>
            <ListItemAvatar>
              <ConventionLogoSquare convention={convention} />
            </ListItemAvatar>
            <ListItemText
              primary={convention.name}
              secondary={`${formatConventionDate(convention)} • ${formatLocation(convention)}`}
            />
          </ListItemButton>
        ))}
      </List>
      {allConventions.length === 0 && (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>No conventions available.</p>
        </div>
      )}
    </FullScreenDialog>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").ConventionId[]} props.value - Selected convention IDs
 * @param {function(import("@seabrookstudios/pitch2table-core").ConventionId|null): void} props.onChange - Callback when selection changes
 * @param {number} props.sessionDate - Session date in milliseconds
 * @param {string} [props.secondary] - Secondary text to display in the dialog
 */
export const SelectConventionDialogForSession = ({ value, onChange, sessionDate, secondary = "" }) => {
  const { data: profile } = useMyProfile();
  const { data: allConventions = [], isLoading } = useUserHistoricalConventions(profile?.id);
  const [selectedConventions, setSelectedConventions] = useState(value);

  // Filter events to those within a week either side of the session date
  const filteredEvents = useMemo(() => {
    if (!sessionDate) {
      return allConventions;
    }

    const sessionDateTime = DateTime.fromMillis(sessionDate);
    const weekBefore = sessionDateTime.minus({ weeks: 1 });
    const weekAfter = sessionDateTime.plus({ weeks: 1 });

    return allConventions.filter((event) => {
      if (!event.startDate) return false;

      const eventDate = DateTime.fromMillis(event.startDate);
      return eventDate >= weekBefore && eventDate <= weekAfter;
    });
  }, [allConventions, sessionDate]);

  /**
   * @param {import("@seabrookstudios/pitch2table-core").ConventionId} conventionId
   */
  const handleSelectConvention = (conventionId) => {
    if (selectedConventions.includes(conventionId)) {
      onChange(null);
      setSelectedConventions([]);
    } else {
      onChange(conventionId);
      setSelectedConventions([conventionId]);
    }
  };

  const handleSave = () => {
    if (selectedConventions.length === 0) {
      onChange(null);
      return;
    }

    onChange(selectedConventions[0]);
  };

  const OnAddOverlay = () => (
    <FilterableConventionsList
      conventions={filteredEvents}
      selectedConventions={selectedConventions}
      onToggleConvention={handleSelectConvention}
    />
  );

  const selectedConventionObjects = filteredEvents.filter((convention) => selectedConventions.includes(convention.id));
  return (
    <SelectableListEntriesDialog
      title="Linked Convention"
      secondary={secondary}
      handleSave={handleSave}
      OnAddOverlay={OnAddOverlay}
      isLoading={isLoading}
      multiple={false}
      selectedCount={selectedConventionObjects.length}
    >
      {selectedConventionObjects.map((convention) => (
        <ArrowListItem
          key={convention.id}
          to={Path.events().event(convention.id)}
          primary={convention.name}
          secondary={`${formatConventionDate(convention)} • ${formatLocation(convention)}`}
          Left={() => <ConventionLogoSquare convention={convention} width={48} height={48} />}
        />
      ))}
    </SelectableListEntriesDialog>
  );
};

export const SelectConventionDialogForPlayTest = SelectConventionDialogForSession;
